import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { collection, getDocs, addDoc, deleteDoc, doc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaEnvelope, FaMapMarkerAlt, FaTrash, FaPaperPlane } from "react-icons/fa";
import { FiCheckCircle, FiX, FiAlertCircle } from "react-icons/fi";

const ContactMessages: React.FC = () => {
  const [messages, setMessages] = useState<
    { id: string; name: string; email: string; location: string; budget: string; message: string }[]
  >([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    location: "",
    budget: "",
    message: "",
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [touched, setTouched] = useState<{[key: string]: boolean}>({});

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });
    return () => unsubscribe();
  }, [auth]);

  const fetchMessages = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "contacts"));
      const fetchedMessages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name || "N/A",
        email: doc.data().email || "N/A",
        location: doc.data().location || "N/A",
        budget: doc.data().budget || "N/A",
        message: doc.data().message || "No message provided",
      }));
      setMessages(fetchedMessages);
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchMessages();
    }
  }, [isLoggedIn]);

  const validateField = (name: string, value: string) => {
    const newErrors = { ...errors };

    switch(name) {
      case 'name':
        if (!value.trim()) {
          newErrors.name = 'Name is required';
        } else if (value.length < 2) {
          newErrors.name = 'Name must be at least 2 characters';
        } else {
          delete newErrors.name;
        }
        break;
      case 'email':
        if (!value.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Invalid email format';
        } else {
          delete newErrors.email;
        }
        break;
      case 'message':
        if (!value.trim()) {
          newErrors.message = 'Message is required';
        } else if (value.length < 10) {
          newErrors.message = 'Message must be at least 10 characters';
        } else {
          delete newErrors.message;
        }
        break;
    }

    setErrors(newErrors);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate all required fields
    validateField('name', formData.name);
    validateField('email', formData.email);
    validateField('message', formData.message);

    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      await addDoc(collection(db, "contacts"), formData);
      setFormData({ name: "", email: "", location: "", budget: "", message: "" });
      setErrors({});
      setTouched({});
      setSubmitted(true);
      if (isLoggedIn) fetchMessages();
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  const handleDeleteMessage = async (id: string) => {
    try {
      await deleteDoc(doc(db, "contacts", id));
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (err) {
      console.error("Error deleting message:", err);
    }
  };

  return (
    <section className="section-padding relative" id="contact">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

      <div className="max-w-6xl mx-auto relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="section-badge"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Say Hello
          </motion.div>
          <h2 className="section-title">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <p className="section-subtitle">
            Have a project in mind? Let's discuss how I can help
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            className="lg:col-span-2 space-y-8"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Let's discuss your project
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                I'm always interested in hearing about new projects and opportunities.
                Feel free to reach out if you'd like to collaborate.
              </p>
            </div>

            <div className="space-y-4">
              <motion.div
                className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-surface-800/50 hover:bg-primary-50 dark:hover:bg-primary-950/30 transition-colors duration-300 group"
                whileHover={{ x: 4 }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-950/50 flex items-center justify-center text-primary-600 dark:text-primary-400 group-hover:scale-110 transition-transform duration-300">
                  <FaEnvelope size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Email</p>
                  <p className="font-medium text-gray-900 dark:text-white">mmfmrifath@gmail.com</p>
                </div>
              </motion.div>
              <motion.div
                className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-surface-800/50 hover:bg-primary-50 dark:hover:bg-primary-950/30 transition-colors duration-300 group"
                whileHover={{ x: 4 }}
              >
                <div className="w-12 h-12 rounded-xl bg-accent-100 dark:bg-accent-950/50 flex items-center justify-center text-accent-600 dark:text-accent-400 group-hover:scale-110 transition-transform duration-300">
                  <FaMapMarkerAlt size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Location</p>
                  <p className="font-medium text-gray-900 dark:text-white">Available Worldwide</p>
                </div>
              </motion.div>
            </div>

            {/* Status indicator */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent-50 dark:bg-accent-950/50 border border-accent-200 dark:border-accent-800"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent-500" />
              </span>
              <span className="text-sm font-medium text-accent-700 dark:text-accent-300">
                Currently available for work
              </span>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="glass-card p-8">
              {submitted && (
                <motion.div
                  className="fixed top-20 right-4 left-4 sm:left-auto sm:right-8 bg-green-500 text-white px-5 py-3 sm:px-6 sm:py-4 rounded-xl shadow-2xl flex items-center gap-3 z-50"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <FiCheckCircle size={24} />
                  <div>
                    <p className="font-semibold">Message sent successfully!</p>
                    <p className="text-sm text-green-100">I'll get back to you soon.</p>
                  </div>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="ml-4 hover:bg-green-600 p-1 rounded focus-visible-ring"
                    aria-label="Dismiss notification"
                  >
                    <FiX size={18} />
                  </button>
                </motion.div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 transition-colors duration-300 ${
                      focusedField === "name" ? "text-primary-600 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                    }`}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (touched.name) validateField('name', e.target.value);
                      }}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => {
                        setFocusedField(null);
                        setTouched({ ...touched, name: true });
                        validateField('name', formData.name);
                      }}
                      placeholder="Your name"
                      className={`input-field focus-visible-ring ${
                        touched.name && errors.name ? 'border-red-500 focus:ring-red-500' : ''
                      }`}
                      required
                    />
                    {touched.name && errors.name && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <FiAlertCircle size={14} />
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 transition-colors duration-300 ${
                      focusedField === "email" ? "text-primary-600 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                    }`}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (touched.email) validateField('email', e.target.value);
                      }}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => {
                        setFocusedField(null);
                        setTouched({ ...touched, email: true });
                        validateField('email', formData.email);
                      }}
                      placeholder="your@email.com"
                      className={`input-field focus-visible-ring ${
                        touched.email && errors.email ? 'border-red-500 focus:ring-red-500' : ''
                      }`}
                      required
                    />
                    {touched.email && errors.email && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <FiAlertCircle size={14} />
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 transition-colors duration-300 ${
                      focusedField === "location" ? "text-primary-600 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                    }`}>
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      onFocus={() => setFocusedField("location")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Your city"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 transition-colors duration-300 ${
                      focusedField === "budget" ? "text-primary-600 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                    }`}>
                      Budget
                    </label>
                    <input
                      type="text"
                      value={formData.budget}
                      onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                      onFocus={() => setFocusedField("budget")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Project budget"
                      className="input-field"
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1.5 transition-colors duration-300 ${
                    focusedField === "message" ? "text-primary-600 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                  }`}>
                    Message *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => {
                      setFormData({ ...formData, message: e.target.value });
                      if (touched.message) validateField('message', e.target.value);
                    }}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => {
                      setFocusedField(null);
                      setTouched({ ...touched, message: true });
                      validateField('message', formData.message);
                    }}
                    placeholder="Tell me about your project..."
                    className={`input-field min-h-[140px] focus-visible-ring ${
                      touched.message && errors.message ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                    rows={5}
                    required
                  />
                  {touched.message && errors.message && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <FiAlertCircle size={14} />
                      {errors.message}
                    </p>
                  )}
                </div>
                <motion.button
                  type="submit"
                  className="btn-primary w-full justify-center"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <FaPaperPlane size={14} />
                  Send Message
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Messages List (Admin Only) */}
        {isLoggedIn && messages.length > 0 && (
          <motion.div
            className="mt-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Messages ({messages.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={msg.id}
                  className="glass-card p-5 relative group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{msg.name}</h4>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{msg.email}</p>
                    </div>
                    <div className="admin-controls">
                      <motion.button
                        onClick={() => handleDeleteMessage(msg.id)}
                        className="btn-icon-sm btn-danger"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        aria-label={`Delete message from ${msg.name}`}
                      >
                        <FaTrash size={14} />
                      </motion.button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                    {msg.message}
                  </p>
                  {msg.budget !== "N/A" && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                      <span className="text-xs text-gray-400 font-medium">Budget: {msg.budget}</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ContactMessages;
